import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/formfields/TextInput";
import type { TeamDetail, TeamPayload } from "@/interface/masterManagement";
import { useCreateTeam, useUpdateTeam } from "../hooks/useTeam";

const teamSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Team Name is required")
    .max(255, "Team Name cannot exceed 255 characters"),
});

export type TeamFormValues = z.infer<typeof teamSchema>;

interface TeamFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: TeamDetail | null;
}

export const TeamFormModal: React.FC<TeamFormModalProps> = ({
  isOpen,
  onClose,
  editData,
}) => {
  const createMutation = useCreateTeam();
  const updateMutation = useUpdateTeam();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<TeamFormValues>({
    resolver: zodResolver(teamSchema),
    defaultValues: {
      name: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        reset({
          name: editData.team_name || "",
        });
      } else {
        reset({
          name: "",
        });
      }
    }
  }, [isOpen, editData, reset]);

  const onSubmit = async (values: TeamFormValues) => {
    try {
      const payload: TeamPayload = {
        team_name: values.name,
        name: values.name,
      };

      if (editData) {
        await updateMutation.mutateAsync({ id: editData.id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch {
      // Error handled by query
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800">
          <DialogTitle>{editData ? "Edit Team" : "Add Team"}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-6 space-y-5 bg-slate-50/50 dark:bg-slate-900/50">
            <TextInput
              control={control}
              name="name"
              label="Team Name"
              placeholder="e.g. Frontend Development Team"
              required
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isSubmitting ||
              createMutation.isPending ||
              updateMutation.isPending
                ? "Saving..."
                : editData
                  ? "Save Changes"
                  : "Add Team"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TeamFormModal;
